import type { HouseholdStateRow } from './household.api';

/**
 * Cluster H v2 — the 6 visual states surfaced per household member in the
 * inline panel. household_members.status (the underlying DB column) only
 * has 6 contact-tracker states (pending, contacted, scheduled, screened,
 * declined, unreachable), so the v2 lifecycle states are DERIVED on the
 * frontend from a combination of:
 *   - hm.status
 *   - the (optional) sub-screening row's lifecycle columns
 *   - the latest HOUSEHOLD_SCREENING_* audit row for this hmId
 *
 * Most-specific-first evaluation (LD-H2-08 + edge cases):
 *   1. declined            — terminal; agent confirmed.
 *   2. rejected_by_uploader — terminal; uploader flagged not eligible.
 *   3. validated           — terminal billable; uploader confirmed FindHelp upload.
 *   4. completed           — submitted, awaiting uploader action.
 *   5. in_progress         — sub-screening exists, not yet submitted.
 *   6. pending             — no sub-screening yet (default; also where
 *                            reaper-deleted subs land via LEFT JOIN nulls).
 */
export type HouseholdDerivedState =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'validated'
  | 'declined'
  | 'rejected_by_uploader';

export function deriveHouseholdScreeningState(row: HouseholdStateRow): HouseholdDerivedState {
  // 1. declined — column status or audit row wins; either is terminal.
  if (row.hmStatus === 'declined' || row.lastAuditAction === 'HOUSEHOLD_SCREENING_DECLINED') {
    return 'declined';
  }

  // 2. rejected_by_uploader — sub-screening flagged OR audit row says rejected.
  if (
    row.subFindhelpFlaggedError != null ||
    row.lastAuditAction === 'HOUSEHOLD_SCREENING_UPLOADER_REJECTED'
  ) {
    return 'rejected_by_uploader';
  }

  // 3. validated — uploader marked findhelp_uploaded_at AND the screening
  //    actually went to FindHelp (not the no_medicaid alt path, which counts
  //    as completed-but-not-billable in v2 UX).
  if (row.subFindhelpUploadedAt != null && !row.subFindhelpNoMedicaid) {
    return 'validated';
  }

  // 4. completed — submitted by the agent, awaiting uploader. Stays here
  //    indefinitely until uploader acts (validates -> 'validated', or flags
  //    -> 'rejected_by_uploader'). is_submitted=true is the gate.
  if (row.subIsSubmitted === true) {
    return 'completed';
  }

  // 5. in_progress — sub-screening row exists (LEFT JOIN matched) but
  //    is_submitted is still false. Agent has the panel open or is mid-flow.
  if (row.subScreeningId != null) {
    return 'in_progress';
  }

  // 6. pending — no sub-screening row joined. This is also where reaper-
  //    deleted sub-screenings land: the backend LEFT JOIN filters by
  //    deleted_at IS NULL, so a reaped sub produces null fields and the
  //    member returns to 'pending' (re-openable).
  return 'pending';
}

/**
 * Helper — once a member is in one of the 3 terminal states (declined,
 * rejected_by_uploader, validated), the inline panel must NOT show an
 * expand-screening control; only a status badge.
 */
export function isTerminalState(state: HouseholdDerivedState): boolean {
  return state === 'declined' || state === 'rejected_by_uploader' || state === 'validated';
}
